import { Container } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Stack } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { init, update } from "../../redux/actions/evaluationList";
import Footer from "../common/Footer";
import Header from "../common/Header";
import { getGradeLabel, generateGradeRangeOptions } from "../common/constants"

export default function UpdateEvaluationList() {
  const [rows, setRows] = useState([]);

  const dispatch = useDispatch();
  const [abolitionFlgLabelFalse, abolitionFlgLabelTrue] = useSelector(state => state.evaluationList.abolitionFlgTexts);
  const abolitionFlgValueTrue = useSelector(state => state.evaluationList.abolitionFlgValues[1]);
  const listRow = useSelector(state => state.evaluationList.rows);
  const isDisableMap = useSelector(state => state.evaluationList.isDisableMap);

  useEffect(
    () => {
      if (listRow) {
        setRows(listRow);
      }
    },
    [listRow]
  );

  //初期処理
  useEffect(
    () => {
      dispatch(init());
    },
    [dispatch]
  );

  const handleGridDataChange = params => {
    const { id, field, value } = params;

    setRows(prevData =>
      prevData.map(row => {
        if (row.id !== id) return row;

        const updatedRow = { ...row, [field]: value };

        if (field === 'numberOfGrades') {
          const opts = generateGradeRangeOptions(value).map(o => o.value);
          if (!opts.includes(updatedRow.gradeRange)) {
            updatedRow.gradeRange = opts[0];
          }
        }

        return updatedRow;
      })
    );
  };


  const updateSubmit = () => {
    const invalidRows = rows
      .filter(row => {
        const originalRow = listRow.find(r => r.id === row.id);
        return (
          originalRow &&
          isDisableMap[row.listId] === true &&
          (row.numberOfGrades !== originalRow.numberOfGrades || row.gradeRange !== originalRow.gradeRange)
        );
      })
      .map(row => row.listName);

    if (invalidRows.length > 0) {
      alert(
        `この評価リストは使用中のため、評価等級数または評価等級範囲を変更できません: ${invalidRows.join(", ")}`
      );
      setRows(listRow);
      return;
    }

    dispatch(update(rows));
  };

  const columns = [
    { field: "listId", headerName: "リストID", width: 100, editable: true, headerClassName: "headerColor", flex: 1 },
    { field: "listName", headerName: "リスト名", editable: true, width: 117, headerClassName: "headerColor", flex: 1 },
    {
      field: "numberOfGrades",
      headerName: "評価等級数",
      type: "number",
      editable: true,
      headerAlign: "center",
      align: "center",
      width: 110,
      headerClassName: "headerColor",
      flex: 1,
      valueFormatter: params => `${params.value}`, 
      preProcessEditCellProps: params => {
        const value = Number(params.props.value);
        const isValidNumber = !isNaN(value) && Number.isInteger(Number(value));
        const isValidRange = value >= 3 && value <= 6;

        return {
          ...params.props,
          error: !(isValidNumber && isValidRange),
        };
      }
    },
    {
      field: "gradeRange",
      headerName: "評価等級範囲",
      type: "singleSelect",
      editable: true,
      align: "center",
      headerAlign: "center",
      width: 120,
      headerClassName: "headerColor",
      flex: 1,
      valueOptions: params => generateGradeRangeOptions(params.row.numberOfGrades),
      renderCell: params => (
        <div>
          {getGradeLabel(params.value)}
        </div>
      )
    },
    {
      field: "abolition",
      headerName: "廃止",
      type: "singleSelect",
      valueOptions: [{ value: abolitionFlgValueTrue, label: abolitionFlgLabelTrue }],
      renderCell: params =>
        <div>
          {params.value === abolitionFlgValueTrue ? abolitionFlgLabelTrue : abolitionFlgLabelFalse}
        </div>,
      editable: true,
      width: 120,
      headerClassName: "headerColor",
      flex: 1
    },
    {
      field: "registeredDate",
      headerName: "登録日時",
      type: "dateTime",
      editable: false,
      width: 180,
      headerClassName: "headerColor",
      renderCell: params =>
        <div>
          {params.value
            ? new Date(params.value).toLocaleString("ja-JP", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit"
              })
            : ""}
        </div>,
      flex: 1
    },
    {
      field: "updatedDate",
      headerName: "更新日時",
      type: "dateTime",
      editable: false,
      width: 180,
      headerClassName: "headerColor",
      renderCell: params =>
        <div>
          {params.value
            ? new Date(params.value).toLocaleString("ja-JP", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit"
              })
            : ""}
        </div>,
      flex: 1
    }
  ];

  return (
    <Box>
      <Header />
      <Container>
        <Box
          sx={{
            marginTop: 4,
            marginBottom: 2,
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <Typography component="h3" variant="h7" align="left">
            評価リストマスタ更新
          </Typography>
        </Box>
        <Box
          sx={{
            mt: 1,
            alignItems: "left",
            justifyContent: "left",
            paddingLeft: 2
          }}
        >
          <Typography component="h4" variant="h7" align="left">
            評価リスト情報の登録または更新を行う
          </Typography>
        </Box>
        <Box
          sx={{
            margin: 2,
            display: "flex",
            flexDirection: "column",
            bgcolor: "#fff9c4"
          }}
        >
          <Box sx={{ alignItems: "center", justifyContent: "center", m: 2 }}>
            <Container maxWidth="md">
              <div
                style={{
                  height: 330,
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <DataGrid
                  sx={{
                    bgcolor: "white",
                    "& .headerColor": {
                      backgroundColor: "#ffcc80"
                    }
                  }}
                  rows={rows}
                  columns={columns}
                  hideFooter={true}
                  onCellEditCommit={handleGridDataChange}
                />
              </div>
            </Container>
          </Box>
        </Box>
        <Container maxWidth="sm">
          <Stack sx={{}} direction="row" spacing={5} justifyContent="center">
            <Button variant="contained" onClick={updateSubmit} sx={{ bgcolor: "#82b1ff", width: "50%" }}>
              更新
            </Button>
          </Stack>
        </Container>

        <Box sx={{ alignItems: "right", justifyContent: "right", m: 2, display: "flex" }}>
          <Button variant="contained" size="medium" href="/AdminMenu" sx={{ bgcolor: "#82b1ff", marginLeft: "auto" }}>
            戻る
          </Button>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
}
